import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addCalendarLink } from "../api.js";

/**
 * Adding a calendar subscription - a published ICS link.
 *
 * Two uses, deliberately one feature: the owner's own calendar where an
 * organization will not approve the app, and somebody else's published link
 * whose busy time should count too. "Subscription" covers both without the UI
 * having to ask which it is.
 *
 * The cost is staleness, which is stated here rather than discovered later.
 */
export function AddCalendarLink() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");

  const add = useMutation({
    mutationFn: () => addCalendarLink(url, label),
    onSuccess: () => {
      setUrl("");
      setLabel("");
      setOpen(false);
      return queryClient.invalidateQueries({ queryKey: ["connections"] });
    },
  });

  if (!open) {
    return (
      <button type="button" className="secondary" onClick={() => setOpen(true)}>
        Add a subscription
      </button>
    );
  }

  return (
    <form
      className="linkform"
      onSubmit={(event) => {
        event.preventDefault();
        add.mutate();
      }}
    >
      <h2>Add a calendar subscription</h2>
      <p className="helper">
        Subscribe to any published calendar link and its busy time counts like a connected
        calendar. Use it for your own calendar when an organization won&rsquo;t approve
        access, or for someone else&rsquo;s published availability. Nothing needs
        approving, and a subscription is read-only.
      </p>

      <label className="field">
        <span>Published calendar link (ICS)</span>
        <input
          type="url"
          required
          placeholder="https://outlook.office365.com/owa/calendar/…/calendar.ics"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
        />
      </label>

      <label className="field">
        <span>What to call it</span>
        <input
          type="text"
          required
          placeholder="Northwind Traders"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
        />
      </label>

      <details>
        <summary>Where to find this link</summary>
        <div className="helper">
          <p>
            <strong>Outlook / Microsoft 365:</strong> Calendar → Settings → Shared
            calendars → Publish a calendar → choose your calendar →{" "}
            <em>Can view when I&rsquo;m busy</em> → Publish, then copy the{" "}
            <strong>ICS</strong> link (not the HTML one).
          </p>
          <p>
            <strong>Google Calendar:</strong> Settings → your calendar → Integrate
            calendar → copy the <strong>Secret address in iCal format</strong>.
          </p>
          <p>
            Treat the link like a password: anyone holding it can read that calendar. Some
            organizations disable publishing entirely — if yours has, this option
            won&rsquo;t be available to you.
          </p>
        </div>
      </details>

      {add.isError ? (
        <div className="notice notice-error" role="alert">
          <strong>We couldn&rsquo;t add that subscription</strong>
          <span>{add.error.message}</span>
        </div>
      ) : null}

      <div className="row">
        <button type="submit" className="primary" disabled={add.isPending}>
          {add.isPending ? "Checking the link…" : "Add subscription"}
        </button>
        <button type="button" className="ghost" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>

      <p className="helper">
        Subscriptions refresh on the publisher&rsquo;s own schedule, so they can lag the
        real calendar. Where you can connect an account directly, that stays more
        accurate.
      </p>
    </form>
  );
}
